import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { colors, formes, radius, shadow } from '../theme/tokens';
import { BLOC_LTR } from '../i18n/sens';

export interface InterrupteurProps {
  value: boolean;
  onValueChange: (valeur: boolean) => void;
  // Obligatoire : un interrupteur sans libellé n'est qu'un « bouton » muet pour un lecteur d'écran.
  label: string;
  disabled?: boolean;
  testID?: string;
}

/**
 * Interrupteur dessiné par l'app (46 × 28), identique sur iOS, Android et le web. Le `Switch` natif
 * n'a ni cette taille ni cette couleur, et rend différemment selon la plateforme.
 *
 * La piste garde le sens de gauche à droite en arabe : « allumé » reste du côté où tous les
 * interrupteurs du système l'affichent.
 */
export function Interrupteur({ value, onValueChange, label, disabled = false, testID }: InterrupteurProps) {
  return (
    <Pressable
      testID={testID}
      accessibilityRole="switch"
      accessibilityLabel={label}
      accessibilityState={{ checked: value, disabled }}
      disabled={disabled}
      onPress={() => onValueChange(!value)}
      // La piste fait 28 pt de haut : la zone tactile monte à 44 sans changer le dessin.
      hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
    >
      <View
        testID={testID ? `${testID}-piste` : undefined}
        style={[
          styles.piste,
          BLOC_LTR,
          { backgroundColor: value ? colors.electricDeep : colors.grey200 },
          value ? styles.allumee : styles.eteinte,
          disabled && styles.inactive,
        ]}
      >
        <View style={styles.pouce} />
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  piste: {
    width: formes.interrupteur.largeur,
    height: formes.interrupteur.hauteur,
    borderRadius: radius.pill,
    padding: (formes.interrupteur.hauteur - formes.interrupteur.pouce) / 2,
    flexDirection: 'row',
    alignItems: 'center',
  },
  allumee: { justifyContent: 'flex-end' },
  eteinte: { justifyContent: 'flex-start' },
  inactive: { opacity: 0.5 },
  pouce: {
    width: formes.interrupteur.pouce,
    height: formes.interrupteur.pouce,
    borderRadius: radius.pill,
    backgroundColor: colors.white,
    ...shadow.card,
  },
});
